<template>
  <label class="nice-radio payment-option-radio">
    <input
      type="radio"
      :value="option.id"
      name="payment-option"
      :disabled="disabled"
      :checked="isSelected"
      @change="selectOption"
    />
    <div class="payment-option">
      <div class="payment-option__vertical-line"></div>
      <h4 class="payment-option__title payment-option__item">{{ option.name ? option.name : 'Option ' + order }}</h4>
      <div class="payment-option__price payment-option__item">
        <PercentSVG />
        <span>Amount ${{ totalAmountForPay.toFixed(2) }}</span>
      </div>

      <div class="payment-option__schedule">
        <template v-if="option.schedule_option.timeSchedule == 'Charge Immediately'">
          <div class="payment-option__item">
            <FileSVG />
            <span>{{ option.schedule_option.timeSchedule }}</span>
          </div>
        </template>

        <template v-else>
          <!-- ── Standard schedule (allowCustomSchedule OFF) ── -->
          <template v-if="!option.schedule_option.allowCustomSchedule">
            <div class="payment-option__item">
              <SplitSVG />
              <span>{{ option.schedule_option.timeSchedule }} {{ option.schedule_option.paymentAmountRange.from }}-{{ option.schedule_option.paymentAmountRange.to }}</span>
              <b-form-spinbutton
                id="range"
                v-model="amountOfPayments"
                :min="option.schedule_option.paymentAmountRange.from"
                :max="option.schedule_option.paymentAmountRange.to"
                inline
                :disabled="disabled"
                @input="selectAmountOfPayments"
              />
            </div>

            <div class="payment-option__item flex-nowrap">
              <CalendarSVG />
              <span>Start Date</span>
              <date-picker
                v-model="startDate"
                input-class="payment-option__start-date"
                valueType="MM/DD/YYYY"
                format="MM/DD/YYYY"
                placeholder="Select"
                :disabled="disabled"
                :disabled-date="disabledStartDates"
                @input="selectStartDate"
              />
            </div>
            <div class="payment-option__item">
              <TimeSVG />
              <span>Ending {{ option.schedule_option.endDate }}</span>
            </div>
          </template>

          <!-- ── Custom schedule (allowCustomSchedule ON) ── -->
          <template v-else>
            <div class="payment-option__item">
              <SplitSVG />
              <span>
                {{ option.schedule_option.paymentAmountRange.from }}-{{ option.schedule_option.paymentAmountRange.to }} payments,
                due by {{ option.schedule_option.endDate }}
              </span>
            </div>

            <!-- Rows -->
            <div class="payment-option__custom-schedule">
              <div
                v-for="(row, idx) in customSchedule"
                :key="idx"
                class="payment-option__custom-row"
              >
                <span class="payment-option__custom-row-num">#{{ idx + 1 }}</span>

                <date-picker
                  v-model="row.date"
                  input-class="payment-option__start-date"
                  valueType="YYYY-MM-DD"
                  format="MM/DD/YYYY"
                  placeholder="Date"
                  :disabled="disabled"
                  :disabled-date="(date) => disabledCustomDate(date, idx)"
                  @input="onCustomScheduleChange"
                />

                <span class="payment-option__custom-row-sep">$</span>

                <input
                  v-model.number="row.amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  class="payment-option__custom-amount"
                  placeholder="0.00"
                  :disabled="disabled || idx === customSchedule.length - 1"
                  @keydown="e => e.key === '-' && e.preventDefault()"
                  @input="e => { if (row.amount < 0) { row.amount = 0; e.target.value = 0 } onCustomScheduleChange() }"
                />

                <button
                  v-if="!disabled && customSchedule.length > option.schedule_option.paymentAmountRange.from"
                  type="button"
                  class="payment-option__custom-remove"
                  @click.prevent="removeCustomRow(idx)"
                >✕</button>
              </div>

              <div class="payment-option__custom-row payment-option__info-row">
                <!-- Add row button -->
                <button
                  v-if="!disabled && customSchedule.length < option.schedule_option.paymentAmountRange.to"
                  type="button"
                  class="payment-option__custom-add"
                  @click.prevent="addCustomRow"
                >
                  + Add Payment
                </button>

                <!-- Total-matches hint (remaining is always 0 — last row is auto-filled) -->
                <div
                  v-if="remainingAmount === 0 && !customScheduleError"
                  class="payment-option__custom-remaining payment-option__custom-remaining--ok ml-auto"
                >
                  ✓ Total matches
                </div>
              </div>

              <!-- Validation messages -->
              <div
                v-if="isSelected && customScheduleError && !disabled"
                class="payment-option__disclaimer payment-option__custom-validation"
              >
                {{ customScheduleError }}
              </div>
            </div>
          </template>
        </template>
      </div>

      <div class="payment-option__method payment-option__item">
        <DollarSVG />
        <span>Payment method</span>
        <v-select
          v-model="selectedMethod"
          class="payment-option__select"
          placeholder="Select"
          :options="option.method"
          :searchable="false"
          :clearable="false"
          :disabled="disabled"
          @input="selectMethod"
        />
      </div>

      <div
        v-if="isSelected && isInvalidOptionSettings && (!disabled || option.startDate)"
        class="payment-option__disclaimer"
      >
        * Payments must be complete by {{ option.schedule_option.endDate }}. Please adjust the start date and amount of payments.
      </div>
    </div>
  </label>
</template>

<script>
import moment from 'moment'

import { BFormSpinbutton } from 'bootstrap-vue'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

import PercentSVG from '@/assets/images/icons/percent.svg'
import FileSVG from '@/assets/images/icons/file.svg'
import SplitSVG from '@/assets/images/icons/split.svg'
import CalendarSVG from '@/assets/images/icons/calendar.svg'
import TimeSVG from '@/assets/images/icons/time.svg'
import DollarSVG from '@/assets/images/icons/dollar.svg'

export default {
  components: {
    BFormSpinbutton,
    DatePicker,
    vSelect,
    PercentSVG,
    FileSVG,
    SplitSVG,
    CalendarSVG,
    TimeSVG,
    DollarSVG,
  },
  props: {
    selectedOption: {
      type: Number,
      default: 0,
    },
    option: {
      type: Object,
      default: () => {},
    },
    order: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    totalAmountForPay: {
      type: Number,
      default: 0,
    },
  },
  data() {
    const minPayments = this.option?.schedule_option?.paymentAmountRange?.from ?? 1
    const initialRows = []
    for (let i = 0; i < minPayments; i++) {
      initialRows.push({ date: null, amount: 0 })
    }

    return {
      selectedMethod: this.option.selectedMethod,
      startDate: this.option.startDate,
      amountOfPayments: this.option.amountOfPayments,
      isInvalidOptionSettings: false,
      // Custom schedule state
      customSchedule: this.option.customSchedule?.length
        ? JSON.parse(JSON.stringify(this.option.customSchedule))
        : initialRows,
    }
  },
  computed: {
    isSelected() {
      return this.option.id == this.selectedOption
    },
    isCustomSchedule() {
      return this.option?.schedule_option?.allowCustomSchedule === true
    },
    endDate() {
      return this.option?.schedule_option?.endDate ?? null
    },
    remainingAmount() {
      const scheduled = this.customSchedule.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
      return Math.round((this.totalAmountForPay - scheduled) * 100) / 100
    },
    customScheduleError() {
      if (!this.isCustomSchedule) return null
      const min = this.option.schedule_option.paymentAmountRange.from
      const max = this.option.schedule_option.paymentAmountRange.to

      if (this.customSchedule.length < min) return `Minimum ${min} payment(s) required.`
      if (this.customSchedule.length > max) return `Maximum ${max} payment(s) allowed.`

      for (let i = 0; i < this.customSchedule.length; i++) {
        const row = this.customSchedule[i]
        if (!row.date) return `Payment #${i + 1}: date is required.`
        if ((parseFloat(row.amount) || 0) <= 0) return `Payment #${i + 1}: amount must be greater than 0.`
        if (this.endDate && row.date > moment(this.endDate, 'MM/DD/YYYY').format('YYYY-MM-DD')) {
          return `Payment #${i + 1}: date must be on or before ${this.endDate}.`
        }
      }
      if (this.remainingAmount !== 0) return `Total must equal $${this.totalAmountForPay.toFixed(2)}. Remaining: $${this.remainingAmount.toFixed(2)}`
      return null
    },
  },
  mounted() {
    if (this.selectedOption && this.option) {
      this.selectOption()
    }
  },
  watch: {
    'option.customSchedule': {
      deep: true,
      handler(newVal) {
        if (newVal && newVal.length) {
          this.customSchedule = JSON.parse(JSON.stringify(newVal))
        }
      },
    },
    isInvalidOptionSettings(val) {
      this.optionValidation(val)
    },
    selectedOption(newVal) {
      if (newVal !== null) {
        this.$nextTick(() => {
          const radioButton = this.$el.querySelector(`input[value="${newVal}"]`)
          if (radioButton) {
            const event = new Event('change', { bubbles: true })
            radioButton.dispatchEvent(event)
          }
        })
      }
    },
    totalAmountForPay() {
      if (this.isCustomSchedule) {
        this.syncLastRowAmount()
      }
    },
  },
  methods: {
    // ── Standard schedule methods ────────────────────────────
    selectOption() {
      this.$emit('selectOption', this.option)
      this.optionValidation(this.isInvalidOptionSettings)
    },
    checkInvalidOptionSettings() {
      if (this.option.schedule_option.timeSchedule === 'Split over') {
        const startDate = moment(this.option.startDate, 'MM/DD/YYYY').add(this.option.amountOfPayments - 1, 'M')
        const endDate = moment(this.option.schedule_option.endDate, 'MM/DD/YYYY')
        if (startDate > endDate) {
          this.isInvalidOptionSettings = true
          return
        }
      }
      this.isInvalidOptionSettings = false
    },
    optionValidation(val) {
      this.$emit('optionValidation', { id: this.option.id, hasError: val })
    },
    disabledStartDates(date) {
      const yesterday = Date.now() - 24 * 60 * 60 * 1000
      const endDate = new Date(this.option.schedule_option.endDate)
      const disabledPayDate = !this.option.available_days.includes(date.getDate())
      return yesterday > date || date > endDate || disabledPayDate
    },
    selectMethod(val) {
      this.option.selectedMethod = val
      if (this.isSelected) {
        this.$emit('selectMethod', val)
      }
    },
    selectAmountOfPayments(val) {
      this.option.amountOfPayments = val
      this.checkInvalidOptionSettings()
      if (this.isSelected) {
        this.$emit('updateAmountOfPayments', val)
      }
    },
    selectStartDate(val) {
      this.option.startDate = val
      this.checkInvalidOptionSettings()
      if (this.isSelected) {
        this.startDate = val
        this.$emit('updateStartDate', { optionId: this.option.id, startDate: val })
      }
    },

    // ── Custom schedule methods ──────────────────────────────
    disabledCustomDate(date, currentRowIdx) {
      const yesterday = Date.now() - 24 * 60 * 60 * 1000
      if (date < yesterday) return true
      if (this.endDate) {
        const end = new Date(this.endDate)
        end.setHours(23, 59, 59)
        if (date > end) return true
      }
      const disabledPayDate = !this.option.available_days.includes(date.getDate())
      if (disabledPayDate) return true
      // Disable dates already selected in other rows
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      const usedInOtherRow = this.customSchedule.some((row, idx) => {
        if (idx === currentRowIdx) return false
        return row.date === dateStr
      })
      if (usedInOtherRow) return true
      return false
    },
    addCustomRow() {
      this.customSchedule.push({ date: null, amount: 0 })
      this.syncLastRowAmount()
      this.onCustomScheduleChange()
    },
    removeCustomRow(idx) {
      this.customSchedule.splice(idx, 1)
      this.syncLastRowAmount()
      this.onCustomScheduleChange()
    },
    syncLastRowAmount() {
      if (this.customSchedule.length === 0) return
      const allButLast = this.customSchedule.slice(0, -1)
      const sumOthers = allButLast.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
      const last = this.totalAmountForPay - sumOthers
      this.customSchedule[this.customSchedule.length - 1].amount = Math.round(last * 100) / 100
    },
    onCustomScheduleChange() {
      // Auto-fill last row whenever a non-last row changes
      const allButLast = this.customSchedule.slice(0, -1)
      const sumOthers = allButLast.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
      const last = this.totalAmountForPay - sumOthers
      this.customSchedule[this.customSchedule.length - 1].amount = Math.round(last * 100) / 100

      if (this.isSelected) {
        this.option.customSchedule = this.customSchedule
        this.$emit('updateCustomSchedule', { optionId: this.option.id, customSchedule: this.customSchedule })
        this.optionValidation(!!this.customScheduleError)
      }
    },
  },
}
</script>


